import axios from 'axios';
import language from '@/views/talkytimes/views/message/utils/language';

const TOKENHUB_MODEL = 'hy-mt2-pro';
const TOKENHUB_BASE = '/tokenhub-api';

function resolveLangCode(code) {
  if (!code || code === 'auto') return '';
  return code.split('-')[0];
}

function buildTranslationPrompt(text, targetName) {
  return `Translate to ${targetName}. Only output the translation:\n${String(text)}`;
}

function normalizeTranslation(text, targetName) {
  if (!text) return '';
  let result = String(text).trim();

  const targetMarker = `${targetName}:`;
  if (result.startsWith(targetMarker)) {
    result = result.slice(targetMarker.length).trim();
  }

  return result.trim().replace(/^["'「『]|["'」』]$/g, '');
}

function extractTranslationsApiText(data) {
  if (!data) return '';
  if (typeof data === 'string') return data;

  // TokenHub /api/translations：常见 chat.completion，偶发 choices 再包一层
  const choice = data.choices?.[0];
  if (choice) {
    const choiceContent = choice?.message?.content ?? choice?.text;
    if (typeof choiceContent === 'string' && choiceContent.trim()) {
      return choiceContent;
    }
    // 嵌套 choices（如 choices[0].choices[0].message.content）
    const nested = extractTranslationsApiText(choice);
    if (nested) return nested;
  }

  if (typeof data.translation === 'string') return data.translation;
  if (Array.isArray(data.translation) && data.translation[0]) {
    return String(data.translation[0]);
  }
  if (typeof data.text === 'string') return data.text;
  if (typeof data.translated_text === 'string') return data.translated_text;
  if (typeof data.output_text === 'string') return data.output_text;
  if (data.data) return extractTranslationsApiText(data.data);
  return '';
}

function extractResponsesText(data) {
  if (!data) return '';
  if (typeof data.output_text === 'string' && data.output_text.trim()) {
    return data.output_text;
  }
  if (Array.isArray(data.output)) {
    for (const item of data.output) {
      if (typeof item?.content === 'string' && item.content.trim()) {
        return item.content;
      }
      if (Array.isArray(item?.content)) {
        for (const part of item.content) {
          if (typeof part?.text === 'string' && part.text.trim()) {
            return part.text;
          }
        }
      }
      if (typeof item?.text === 'string' && item.text.trim()) {
        return item.text;
      }
    }
  }
  return '';
}

/**
 * TokenHub 专用翻译接口 /api/translations
 */
export function tokenHubTranslate(text, from = 'auto', to = 'zh') {
  if (!text || !String(text).trim()) {
    return Promise.resolve('');
  }

  const targetCode = resolveLangCode(to) || 'zh';
  const sourceCode = resolveLangCode(from);
  const body = {
    model: TOKENHUB_MODEL,
    text: String(text),
    target: targetCode,
  };
  if (sourceCode) {
    body.source = sourceCode;
  }

  return axios
    .post(`${TOKENHUB_BASE}/api/translations`, body, {
      headers: { 'Content-Type': 'application/json' },
      timeout: 120000,
    })
    .then((response) => {
      const result = extractTranslationsApiText(response.data).trim();
      if (!result) {
        throw new Error('TokenHub 翻译返回结果为空');
      }
      return result;
    });
}

/**
 * TokenHub Responses API 翻译
 */
export function tokenHubResponsesTranslate(text, from = 'auto', to = 'zh') {
  if (!text || !String(text).trim()) {
    return Promise.resolve('');
  }

  const targetCode = resolveLangCode(to) || 'zh';
  const targetName = language.getLangEnglishNameByCode(targetCode);

  return axios
    .post(
      `${TOKENHUB_BASE}/responses`,
      {
        model: TOKENHUB_MODEL,
        instructions: 'You are a translation assistant. Only output the translation.',
        input: buildTranslationPrompt(text, targetName),
        stream: false,
      },
      {
        headers: { 'Content-Type': 'application/json' },
        timeout: 120000,
      }
    )
    .then((response) => {
      const content = extractResponsesText(response.data);
      if (!content) {
        throw new Error('TokenHub Responses 返回结果为空');
      }
      const result = normalizeTranslation(content, targetName);
      if (!result) {
        throw new Error('TokenHub Responses 翻译结果无效');
      }
      return result;
    });
}
